import BootstrapVue from 'bootstrap-vue';
import Vue from 'vue';
import VueAnalytics from 'vue-analytics';
import VueApollo from 'vue-apollo';
import VueI18n from 'vue-i18n';
import VueLogger from 'vuejs-logger';
import VueMeta from 'vue-meta';
import VueRouter from 'vue-router';
import VueScrollTo from 'vue-scrollto';
import VueCookies from 'vue-cookies';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import * as Sentry from '@sentry/browser';

import App from '@/App';
import VueDebounceWatch from '@/plugins/vue-debounce-watch';
import VueEvents from '@/plugins/vue-events';
import VueNotifications from '@/plugins/vue-notifications';
import configureApp from '@/configure';
import rootMixin from '@/mixins/root';
import { capturePageView } from '@/lib/logs';
import { createApolloClient } from '@/apollo';
import { createI18n } from '@/i18n';
import { createRouter } from '@/router';

// External dependencies
Vue.use(VueRouter);
Vue.use(VueMeta);
Vue.use(VueScrollTo);
Vue.use(VueApollo);
Vue.use(BootstrapVue);
Vue.use(VueI18n);
Vue.use(VueCookies);
Vue.use(VueDebounceWatch);
Vue.use(VueEvents);
Vue.use(VueNotifications);

// Setup the logger @link https://www.npmjs.com/package/vuejs-logger
Vue.use(VueLogger, {
  isEnabled: process.env.VUE_ENV === 'client',
  logLevel: process.env.VUE_ENV === 'server' ? 'warn' : process.env.IS_PROD_HOSTNAME ? 'info' : 'debug',
  showConsoleColors: process.env.VUE_ENV === 'server' ? false : !process.env.IS_PROD_HOSTNAME,
});

// And configure the application
Vue.use(configureApp);

// Finally, global components used throughout
Vue.component('font-awesome-icon', FontAwesomeIcon);

const IS_PRODUCTION = process.env.NODE_ENV === 'production';

export default function createApp({ data, computed }) {
  const { apiURL, apiHeaders, hostname } = data;

  const apolloProvider = createApolloClient({ apiURL, apiHeaders });
  const i18n = createI18n();
  const router = createRouter({ hostname });

  if (process.env.VUE_ENV === 'client') {
    Vue.use(VueAnalytics, {
      id: process.env.GA_ANALYTICS_ID,
      router,
      debug: {
        // Mark as disabled in non-production environments
        sendHitTask: IS_PRODUCTION,
      },
    });
  } else {
    Vue.use(VueAnalytics, {
      id: process.env.GA_ANALYTICS_ID,
      // Mark as disabled, so that the dependency adds all it's functions
      // Otherwise SSR could just "break" because of undefined functions etc.
      disabled: true,
    });
  }

  const app = new Vue({
    name: 'root',
    apolloProvider,
    mixins: [ rootMixin ],
    data: {
      fullPageErr: null,
      isCypress: false,
      isNativeApp: false,
      ...data,
    },
    mounted() {
      // Triggers on mount & when $route changes
      this.$watch('$route', route => {
        Sentry.setTag('route-name', route && route.name ? route.name : undefined);
        Promise.resolve(capturePageView.call(this.$root, route)).catch(err => this.$log.error(err));
      }, { immediate: true });
      // Only triggers when $route subsequently changes
      this.$watch('$route', () => this.fullPageErr = null);
    },
    computed: {
      ...computed,
    },
    i18n,
    render: h => h(App),
    router,
  });

  return {
    app,
    i18n,
    router,
    apolloProvider,
  };
}



// WEBPACK FOOTER //
// ./src/main.js